import suneditor, { langs } from "suneditor";
import type { SunEditor } from "suneditor/types";
import { align, backgroundColor, font, fontColor, fontSize, hr, image, link, list, table } from "suneditor/plugins";
import type { _Lang } from "suneditor/langs/_Lang";

import type { SunEditorCustomOptions } from "./sunEditorCustomOptions";

import HabilLink from "./plugins/habilLink";

type EditorChangeParams = {
    $: SunEditor.Deps;
    frameContext: SunEditor.FrameContext;
    data: string;
};

const standardPlugins = [
    align, backgroundColor, font, fontColor, fontSize, hr, image, link, list, table
];

export class SunEditorWrapper {
    private readonly instance: SunEditor.Instance;

    private readonly handleEditorChange = async ({ data }: EditorChangeParams): Promise<void> => {
        this.onChange(data);
    }

    constructor(
        element: HTMLElement,
        private readonly onChange: (content: string) => void,
        customOptions: SunEditorCustomOptions)
    {
        const options: SunEditor.InitOptions = {
            plugins: {
                ...standardPlugins,
                [HabilLink.key]: HabilLink,
            },
            mode: 'classic',
            width: 'auto',
            height: 'auto',
            minHeight: '250px',
            maxHeight: '250px',
            buttonList: [
                ['undo', 'redo'],
                ['font', 'fontSize'],
                ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
                ['fontColor', 'backgroundColor'],
                ['removeFormat'],
                ['outdent', 'indent'],
                ['align', 'hr', 'list', 'table'],
                ['link', 'image', 'preview'],
            ],
            defaultLine: 'div',
            toolbar_sticky: -1,
            statusbar_showPathLabel: false,
            charCounter: true,
            elementBlacklist: 'figure',
            font: {
                items: [
                    'Arial',
                    'Calibri',
                    'Comic Sans MS',
                    'Courier New',
                    'Garamond',
                    'Georgia',
                    'Helvetica Neue',
                    'Impact',
                    'Lucida Console',
                    'Microsoft Sans Serif',
                    'Palatino Linotype',
                    'Roboto',
                    'Segoe UI',
                    'Tahoma',
                    'Times New Roman',
                    'Trebuchet MS',
                    'Verdana',
                ]
            },
            events: {
                onChange: this.handleEditorChange,
            },
        };

        const languages = new Map<string, _Lang>([['nl', langs.nl], ['fr', langs.fr]]);
        options.lang = languages.get(customOptions.languageCode) || langs.en; // Default to English

        if (customOptions.fullScreenAllowed) {
            if (options.buttonList != null) {
                options.buttonList.push(['codeView', 'fullScreen']);
            }
        }

        if (customOptions.insertHabilLinkAllowed) {
            if (options.buttonList != null) {
                options.buttonList.push([HabilLink.key]);
            }
        }

        this.instance = suneditor.create(element, options);
    }

    public updateContents(content: string): void {
        const wysiwyg = this.instance.$.frameContext.get('wysiwyg') as HTMLElement;

        if (wysiwyg) {
            wysiwyg.innerHTML = content;

            this.clearHistory();
        }
    }

    private clearHistory(): void {
        if (this.instance.$.history && typeof this.instance.$.history.push === 'function') {
            // MANDATORY: Sync with the Undo/Redo history
            this.instance.$.history.push(false);

            // Clear the Undo/Redo history
            this.instance.$.history.reset();
        }
    }

    public destroy(): void {
        this.instance?.destroy();
    }
}
